import {
  Component,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CtaButton } from '@components/button-cta.component';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { SvgComponent } from '@components/svg.component';
import { DeliveryRound } from '@models/delivery-round.model';
import { Household } from '@models/foyers/household.model';
import { DeliveryRoundService } from '@services/delivery-round.service';
import { Router } from '@angular/router';
import { loaderComponent } from '@components/app-loader.component';
import { ItemDeliveryRoundHouseholdComponent } from '../item/item-delivery-round-household.component';
import { DeliveryRoundMobileItemComponent } from '../mobile/delivery-round-mobile-item.component';
import { PromptService } from '@services/prompt.service';
import { ToastrService } from 'ngx-toastr';
import { ResponseModel } from '@models/response.model';
import { HouseholdService } from '@services/household.service';
import { ResponsiveService } from '@services/responsive.service';
import { requiredTrimmed } from '../../../form-validator/trim-validator';
import { PreviewOptimise } from '@models/preview-optimise.model';
import { ModalOptimisePreviewComponent } from '@components/Modals/modal-optimise-preview/modal-optimise-preview.component';

@Component({
  selector: 'app-delivery-round-list',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    CtaButton,
    ReactiveFormsModule,
    SvgComponent,
    loaderComponent,
    ReactiveFormsModule,
    ItemDeliveryRoundHouseholdComponent,
    DeliveryRoundMobileItemComponent,
    ModalOptimisePreviewComponent,
  ],
  templateUrl: 'delivery-round-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./delivery-round-list.component.scss'],
})
export class DeliveryRoundListComponent implements OnInit {
  loading: boolean = true;

  roundsService = inject(DeliveryRoundService);
  router = inject(Router);
  toast = inject(ToastrService);
  promptService = inject(PromptService);
  translate = inject(TranslateService);
  householdService = inject(HouseholdService);
  protected responsiveService = inject(ResponsiveService);

  deliveryRounds: DeliveryRound[] = [];
  selectedRounds: DeliveryRound[] = [];

  // Prévisualisation de l'optimisation des tournées
  optimisePreview: PreviewOptimise | null = null;
  isLoadingPreview: boolean = false;
  isApplyingOptimise: boolean = false;

  showInlineDeliveryRoundCreation: boolean = false;
  isCreatingInlineRound: boolean = false;

  // Pour gérer le renommage de tournée
  renamingRoundId: number | null = null;
  isRenamingRound: boolean = false;

  // Pour gérer le drag and drop
  draggedHousehold: {
    household: any;
    position: number;
    roundId: number;
  } | null = null;

  // Pour l'indicateur de position de drop
  dropIndicator: {
    roundId: number;
    position: 'top' | 'bottom' | null;
  } | null = null;

  _inputInlineDeliveryRoundName = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(1)],
  });

  _inputRenameDeliveryRound = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(1), requiredTrimmed],
  });

  @ViewChild('createDeliveryRoundInput')
  createDeliveryRoundInput!: ElementRef<HTMLInputElement>;

  @ViewChild('renameDeliveryRoundInput')
  renameDeliveryRoundInput!: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    this.loadDeliveryRounds();
  }

  loadDeliveryRounds() {
    this.roundsService.listRounds().subscribe({
      next: response => {
        this.deliveryRounds = response;
        this.loading = false;
      },
    });
  }

  selectRound(round: DeliveryRound): void {
    const index = this.selectedRounds.findIndex(r => r.id === round.id);

    if (index !== -1) {
      // Si la tournée est déjà sélectionnée, la désélectionner
      this.selectedRounds.splice(index, 1);
    } else {
      // Si on a déjà 2 tournées sélectionnées, retirer la première
      if (this.selectedRounds.length === 2) {
        this.selectedRounds.shift();
      }
      // Ajouter la nouvelle tournée sélectionnée
      this.selectedRounds.push(round);
    }
  }

  isRoundSelected(round: DeliveryRound): boolean {
    return this.selectedRounds.some(r => r.id === round.id);
  }

  getSelectedRoundIndex(round: DeliveryRound): number {
    return this.selectedRounds.findIndex(r => r.id === round.id);
  }

  /** Permet de créer une tournée de façon inline (depuis la liste) */
  createInlineRound() {
    this.showInlineDeliveryRoundCreation = true;

    setTimeout(() => {
      const input = this.createDeliveryRoundInput.nativeElement;
      if (input) {
        input.focus();
        // scroll to input
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 0);
  }

  /** Prévisualise l'optimisation de la ou des tournée(s) sélectionnée(s) */
  previewOptimizeSelectedRounds(): void {
    if (this.selectedRounds.length === 0) {
      return;
    }

    this.previewOptimise(this.selectedRounds.map(round => round.id));
  }

  /** Prévisualise l'optimisation globale de toutes les tournées */
  optimizeAllRounds(): void {
    const roundIds = this.deliveryRounds
      .filter(round => (round.households?.length ?? 0) > 0)
      .map(round => round.id);

    if (roundIds.length === 0) {
      return;
    }

    this.previewOptimise(roundIds);
  }

  /** Prévisualise l'optimisation globale des tournées demandées */
  private previewOptimise(roundIds: number[]): void {
    this.isLoadingPreview = true;

    this.roundsService.previewOptimise(roundIds).subscribe({
      next: preview => {
        this.optimisePreview = preview;
        this.isLoadingPreview = false;
      },
      error: err => {
        this.isLoadingPreview = false;
        this.toast.error(this.translate.instant(err.message));
      },
    });
  }

  /** Ferme la prévisualisation de l'optimisation */
  closeOptimisePreview(): void {
    this.optimisePreview = null;
  }

  /** Confirme l'optimisation prévisualisée et l'applique côté serveur */
  confirmOptimisePreview(): void {
    const preview = this.optimisePreview;
    if (!preview || this.isApplyingOptimise) {
      return;
    }

    this.isApplyingOptimise = true;
    const roundIds = preview.routes.map(route => route.round_id);

    this.roundsService.optimiseSelected(roundIds).subscribe({
      next: response => {
        this.isApplyingOptimise = false;
        this.optimisePreview = null;
        this.toast.success(this.translate.instant(response.message));
        this.applyOptimisedOrder(preview);
      },
      error: err => {
        this.isApplyingOptimise = false;
        this.toast.error(this.translate.instant(err.message));
      },
    });
  }

  /**
   * Recompose localement les foyers des tournées pour refléter l'optimisation
   * persistée côté serveur : un foyer peut changer de tournée. Les foyers non
   * géolocalisés, absents de la prévisualisation, restent en fin de liste de
   * leur tournée d'origine dans leur ordre actuel.
   */
  private applyOptimisedOrder(preview: PreviewOptimise): void {
    const householdById = new Map<number, Household>();
    this.deliveryRounds.forEach(round =>
      round.households?.forEach(h => householdById.set(h.id, h))
    );

    // Foyers présents dans la prévisualisation, toutes tournées confondues
    const optimisedIds = new Set<number>();
    preview.routes.forEach(route =>
      route.waypoints.forEach(waypoint => {
        if (waypoint.type === 'job' && waypoint.household_id !== null) {
          optimisedIds.add(waypoint.household_id);
        }
      })
    );

    preview.routes.forEach(route => {
      const round = this.deliveryRounds.find(r => r.id === route.round_id);
      if (!round) {
        return;
      }

      const optimised = route.waypoints
        .filter(
          waypoint => waypoint.type === 'job' && waypoint.household_id !== null
        )
        .map(waypoint => householdById.get(waypoint.household_id as number))
        .filter((h): h is Household => h !== undefined);

      const leftovers = (round.households ?? []).filter(
        h => !optimisedIds.has(h.id)
      );

      round.households = [...optimised, ...leftovers];

      const selectedRound = this.selectedRounds.find(
        r => r.id === route.round_id
      );
      if (selectedRound) {
        selectedRound.households = [...round.households];
      }
    });
  }

  /** Gère le début du drag d'un foyer */
  onHouseholdDragStart(
    household: any,
    position: number,
    roundId: number
  ): void {
    this.draggedHousehold = { household, position, roundId };
  }

  /** Gère la fin du drag d'un foyer */
  onHouseholdDragEnd(): void {
    this.draggedHousehold = null;
    this.dropIndicator = null;
  }

  /** Gère le drop d'un foyer sur une nouvelle position */
  onHouseholdDropped(
    targetHousehold: any,
    targetPosition: number,
    roundId: number
  ): void {
    if (!this.draggedHousehold) {
      return;
    }

    const sourceRoundId = this.draggedHousehold.roundId;
    const sourcePosition = this.draggedHousehold.position;
    const householdId = this.draggedHousehold.household.id;

    // Si on dépose dans la même tournée au même endroit, on ne fait rien
    if (sourceRoundId === roundId && sourcePosition === targetPosition) {
      return;
    }

    // Si on change de tournée
    if (sourceRoundId !== roundId) {
      // Appeler l'API AVANT de modifier les tableaux
      const apiPosition = targetPosition + 1;

      // Appeler l'API pour le changement de tournée
      this.householdService
        .reorder({ householdId, deliveryRoundId: roundId, apiPosition })
        .subscribe({
          next: response => {
            this.toast.success(this.translate.instant(response.message));

            // Modifier les tableaux APRÈS le succès de l'API
            this.updateHouseholdsAfterRoundChange(
              sourceRoundId,
              roundId,
              householdId,
              targetPosition
            );
          },
          error: err => {
            console.error(err);
            this.toast.error(this.translate.instant(err.message));
            // En cas d'erreur, ne pas modifier les tableaux
          },
        });
    } else {
      // Même tournée : utiliser la logique existante
      this.setHouseholdPosition(roundId, householdId, targetPosition);
    }
  }

  /**
   * Met à jour les tableaux de foyers après un changement de tournée réussi
   */
  private updateHouseholdsAfterRoundChange(
    sourceRoundId: number,
    targetRoundId: number,
    householdId: number,
    targetPosition: number
  ): void {
    // Retirer le foyer de la tournée source
    const sourceRound = this.deliveryRounds.find(r => r.id === sourceRoundId);
    let draggedHouseholdData: any = null;

    if (sourceRound && sourceRound.households) {
      const householdIndex = sourceRound.households.findIndex(
        h => h.id === householdId
      );
      if (householdIndex !== -1) {
        draggedHouseholdData = sourceRound.households[householdIndex];
        sourceRound.households.splice(householdIndex, 1);
        sourceRound.households = [...sourceRound.households];

        // Mettre à jour aussi dans selectedRounds si nécessaire
        const selectedSourceRound = this.selectedRounds.find(
          r => r.id === sourceRoundId
        );
        if (selectedSourceRound) {
          selectedSourceRound.households = [...sourceRound.households];
        }
      }
    }

    // Ajouter le foyer dans la nouvelle tournée
    const targetRound = this.deliveryRounds.find(r => r.id === targetRoundId);
    if (targetRound && targetRound.households && draggedHouseholdData) {
      targetRound.households.splice(targetPosition, 0, draggedHouseholdData);
      targetRound.households = [...targetRound.households];

      // Mettre à jour aussi dans selectedRounds si nécessaire
      const selectedTargetRound = this.selectedRounds.find(
        r => r.id === targetRoundId
      );
      if (selectedTargetRound) {
        selectedTargetRound.households = [...targetRound.households];
      }
    }
  }

  /**
   * Gère le changement de position via l'éditeur inline
   */
  onHouseholdPositionChanged(
    household: any,
    targetPosition: number,
    roundId: number
  ): void {
    const householdId = household.id;

    // targetPosition est déjà en 0-indexed
    // Passer false pour ne PAS ajuster l'index (saisie manuelle de position absolue)
    this.setHouseholdPosition(roundId, householdId, targetPosition, false);
  }

  /**
   * Permet le drag over sur le conteneur
   */
  onContainerDragOver(event: DragEvent, roundId: number): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }

    if (!this.draggedHousehold) {
      return;
    }

    // Calculer la position relative pour afficher l'indicateur
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const mouseY = event.clientY;
    const relativeY = mouseY - rect.top;
    const containerHeight = rect.height;

    const targetRound = this.deliveryRounds.find(r => r.id === roundId);
    const isEmpty =
      !targetRound?.households || targetRound.households.length === 0;

    // Si la liste est vide, tout le conteneur est une zone de drop
    if (isEmpty) {
      this.dropIndicator = { roundId, position: 'top' };
      return;
    }

    // Calculer la hauteur approximative occupée par les éléments
    // On estime ~120px par item (hauteur moyenne d'un foyer) + espacement
    const householdCount = targetRound.households.length;
    const estimatedItemHeight = 120;
    const estimatedContentHeight = householdCount * estimatedItemHeight;

    // Seuil de détection :
    // - En haut : 15% du conteneur ou 100px (le minimum)
    // - En bas : Si le contenu est plus petit que le conteneur,
    //   on considère tout ce qui est après le dernier élément comme zone de drop bottom
    const topThreshold = Math.min(containerHeight * 0.15, 100);

    // Zone bottom : soit on est dans les 15% du bas du conteneur,
    // soit on est en dessous de la hauteur estimée du contenu (= sous le dernier élément)
    const bottomThreshold = Math.min(containerHeight * 0.15, 100);
    const isAfterContent = relativeY > estimatedContentHeight;

    if (relativeY < topThreshold) {
      this.dropIndicator = { roundId, position: 'top' };
    } else if (
      relativeY > containerHeight - bottomThreshold ||
      isAfterContent
    ) {
      this.dropIndicator = { roundId, position: 'bottom' };
    } else {
      this.dropIndicator = null;
    }
  }

  /**
   * Réinitialise l'indicateur de drop quand on quitte le conteneur
   */
  onContainerDragLeave(event: DragEvent, roundId: number): void {
    // Ne réinitialiser que si on quitte vraiment le conteneur (pas un enfant)
    const target = event.currentTarget as HTMLElement;
    const relatedTarget = event.relatedTarget as HTMLElement;

    // Si relatedTarget est null ou n'est pas un enfant du conteneur
    if (!relatedTarget || !target.contains(relatedTarget)) {
      // Vérifier que c'est bien l'indicateur de cette tournée qu'on réinitialise
      if (this.dropIndicator?.roundId === roundId) {
        this.dropIndicator = null;
      }
    }
  }

  /**
   * Gère le drop sur le conteneur principal
   * Détecte automatiquement si on drop en haut, en bas ou sur un élément spécifique
   */
  onContainerDrop(event: DragEvent, roundId: number): void {
    event.preventDefault();
    event.stopPropagation();

    // Réinitialiser l'indicateur
    this.dropIndicator = null;

    if (!this.draggedHousehold) {
      return;
    }

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const mouseY = event.clientY;
    const relativeY = mouseY - rect.top;
    const containerHeight = rect.height;

    const sourceRoundId = this.draggedHousehold.roundId;
    const householdId = this.draggedHousehold.household.id;

    // Trouver la tournée cible
    const targetRound = this.deliveryRounds.find(r => r.id === roundId);
    if (!targetRound || !targetRound.households) {
      return;
    }

    // Si la liste est vide, placer en première position
    if (targetRound.households.length === 0) {
      if (sourceRoundId !== roundId) {
        this.dropAtPosition(roundId, householdId, 0, sourceRoundId);
      } else {
        this.setHouseholdPosition(roundId, householdId, 0);
      }
      return;
    }

    // Calculer la hauteur approximative occupée par les éléments
    const householdCount = targetRound.households.length;
    const estimatedItemHeight = 120;
    const estimatedContentHeight = householdCount * estimatedItemHeight;

    // Seuil de détection
    const topThreshold = Math.min(containerHeight * 0.15, 100);
    const bottomThreshold = Math.min(containerHeight * 0.15, 100);
    const isAfterContent = relativeY > estimatedContentHeight;

    // Déterminer où on veut dropper
    if (relativeY < topThreshold) {
      // Drop en haut de la liste (première position)
      if (sourceRoundId !== roundId) {
        this.dropAtPosition(roundId, householdId, 0, sourceRoundId);
      } else {
        this.setHouseholdPosition(roundId, householdId, 0);
      }
    } else if (
      relativeY > containerHeight - bottomThreshold ||
      isAfterContent
    ) {
      // Drop en bas de la liste (dernière position)
      // Soit on est dans les 15% du bas du conteneur, soit après le dernier élément
      if (sourceRoundId !== roundId) {
        const targetPosition = targetRound.households.length;
        this.dropAtPosition(
          roundId,
          householdId,
          targetPosition,
          sourceRoundId
        );
      } else {
        const lastPosition = targetRound.households.length;
        this.setHouseholdPosition(roundId, householdId, lastPosition);
      }
    }
    // Sinon, le drop sera géré par les items individuels
  }

  /**
   * Méthode générique pour dropper à une position lors d'un changement de tournée
   */
  private dropAtPosition(
    targetRoundId: number,
    householdId: number,
    targetPosition: number,
    sourceRoundId: number
  ): void {
    const apiPosition = targetPosition + 1;
    // Appeler l'API AVANT de modifier les tableaux
    this.householdService
      .reorder({ householdId, deliveryRoundId: targetRoundId, apiPosition })
      .subscribe({
        next: response => {
          this.toast.success(this.translate.instant(response.message));

          // Modifier les tableaux APRÈS le succès de l'API
          this.updateHouseholdsAfterRoundChange(
            sourceRoundId,
            targetRoundId,
            householdId,
            targetPosition
          );
        },
        error: err => {
          console.error(err);
          this.toast.error(this.translate.instant(err.message));
        },
      });
  }

  /**
   * Change la position d'un foyer dans une tournée
   * @param deliveryRoundId ID de la tournée
   * @param householdId ID du foyer
   * @param targetIndex Position cible (0-indexed)
   * @param adjustForDragDrop Si true, ajuste l'index pour le drag & drop (défaut: true pour compatibilité)
   */
  setHouseholdPosition(
    deliveryRoundId: number,
    householdId: number,
    targetIndex: number,
    adjustForDragDrop: boolean = true
  ): void {
    // Trouver la tournée
    const round = this.deliveryRounds.find(r => r.id === deliveryRoundId);
    if (!round || !round.households) {
      console.error(
        '[setHouseholdPosition] Tournée non trouvée ou sans foyers'
      );
      return;
    }

    // Trouver le foyer et sa position actuelle
    const currentIndex = round.households.findIndex(h => h.id === householdId);
    if (currentIndex === -1) {
      console.error('[setHouseholdPosition] Foyer non trouvé dans la tournée');
      return;
    }

    // Si on est déjà à la bonne position, ne rien faire
    if (currentIndex === targetIndex) {
      return;
    }

    // Réorganiser le tableau
    const household = round.households[currentIndex];
    round.households.splice(currentIndex, 1);

    // Ajuster l'index cible si l'élément source était avant la cible
    // Car après avoir retiré l'élément, tous les indices après ont décalé de -1
    // Cette logique s'applique uniquement pour le drag & drop
    let adjustedTargetIndex = targetIndex;
    if (adjustForDragDrop && currentIndex < targetIndex) {
      adjustedTargetIndex--;
    }

    // S'assurer que l'index est dans les limites valides
    adjustedTargetIndex = Math.max(
      0,
      Math.min(adjustedTargetIndex, round.households.length)
    );

    round.households.splice(adjustedTargetIndex, 0, household);

    // Créer une nouvelle référence pour forcer la détection de changements d'Angular
    round.households = [...round.households];

    // Mettre à jour aussi dans selectedRounds si nécessaire
    const selectedRound = this.selectedRounds.find(
      r => r.id === deliveryRoundId
    );
    if (selectedRound) {
      selectedRound.households = [...round.households];
    }

    // Position pour l'API (1-indexed : première position = 1)
    const apiPosition = adjustedTargetIndex + 1;

    this.householdService
      .reorder({ householdId, deliveryRoundId, apiPosition })
      .subscribe({
        next: response =>
          this.toast.success(this.translate.instant(response.message)),
        error: err => {
          console.error(err);
          this.toast.success(this.translate.instant(err.message));
        },
      });
  }

  renameRound(round: DeliveryRound) {
    // Empêcher la propagation pour ne pas sélectionner la tournée
    this.renamingRoundId = round.id;
    this._inputRenameDeliveryRound.setValue(round.name);

    setTimeout(() => {
      const input = this.renameDeliveryRoundInput?.nativeElement;
      if (input) {
        input.focus();
        input.select();
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 0);
  }

  validateRenameRound() {
    if (
      this._inputRenameDeliveryRound.invalid ||
      this.renamingRoundId === null
    ) {
      return;
    }

    const newName = this._inputRenameDeliveryRound.value;
    const roundId = this.renamingRoundId;
    this.isRenamingRound = true;

    this.roundsService.updateRound({ id: roundId, name: newName }).subscribe({
      next: response => this.handleResponse(response),
      error: err => this.handleError(err),
    });
  }

  cancelRename() {
    this.renamingRoundId = null;
    this._inputRenameDeliveryRound.reset();
  }

  askToDeleteRound(round: DeliveryRound) {
    this.promptService
      .askForConfirmation(
        'delivery_round.confirmation.title',
        'delivery_round.confirmation.delete'
      )
      .then(confirmed => {
        if (confirmed) {
          this.deleteRound(round);
        }
      });
  }

  createRound() {
    this.isCreatingInlineRound = true;
    this.roundsService
      .createRound({ name: this._inputInlineDeliveryRoundName.value })
      .subscribe({
        next: response => this.handleResponse(response),
        error: err => this.handleError(err),
      });
  }

  deleteRound(round: DeliveryRound) {
    this.roundsService.deleteRound(round.id).subscribe({
      next: response => {
        this.handleResponse(response);
      },
    });
  }

  private handleResponse(response: ResponseModel) {
    if (response.success) {
      this.isCreatingInlineRound = false;
      this.isRenamingRound = false;
      this.renamingRoundId = null;
      this.toast.success(this.translate.instant(response.message));
      this.loadDeliveryRounds();
      this.showInlineDeliveryRoundCreation = false;
      this._inputInlineDeliveryRoundName.reset();
    } else {
      this.isCreatingInlineRound = false;
      this.isRenamingRound = false;
      this.renamingRoundId = null;
      this.toast.error(this.translate.instant(response.message));
      this.showInlineDeliveryRoundCreation = false;
      this._inputInlineDeliveryRoundName.reset();
    }
  }

  private handleError(err: any) {
    console.error(err);
    this.toast.error(this.translate.instant(err.message));
    this.isRenamingRound = false;
    this.isCreatingInlineRound = false;
    this.showInlineDeliveryRoundCreation = false;
    this._inputInlineDeliveryRoundName.reset();
  }

  onHouseholdAction($event: any) {
    console.log('onHouseHoldAction:', $event);
  }
}
